import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { NavigationRoute } from '@src/app/QualityBidders/models/navigation-routes.model';
import { LoginService } from '@src/app/QualityBidders/services';
import { InjectionTokens } from '@src/serviceTokens';
import { BehaviorSubject, filter, Subject, Subscription } from 'rxjs';

/** Provided via main.ts serviceProvider(NavService, { lausd: LausdNavService, ... }). Do not use providedIn: 'root'. */
@Injectable()
export class LausdNavService {
  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected router: Router = inject(Router);
  protected auth: AuthService = inject(AuthService);
  protected login: LoginService = inject(InjectionTokens.get(LoginService));

  private _$subnavRoutes: BehaviorSubject<NavigationRoute[]> = new BehaviorSubject<NavigationRoute[]>([]);
  private _$navChanged: Subject<void> = new Subject<void>();

  private subscription = new Subscription();
  get $subnavRoutes() {
    return this._$subnavRoutes.asObservable();
  }

  constructor() {
    this.getSubnavRoutes();
  }

  setSubnavRoutes(routes?: NavigationRoute[]) {
    if (!routes) {
      routes = [];
    }
    this._$subnavRoutes.next(routes);
  }

  clearSubnavRoutes() {
    this._$subnavRoutes.next([]);
  }

  getSubnavRoutes() {
    // Listen to authentication and navigation
    const sub = this.auth.isAuthenticated$.subscribe(authenticated => {
      if (authenticated) {
        // Immediately populate based on current URL
        const urlSection = this.getUrlSection();
        this.setSubnavRoutes(this.getDefaultSubnavRoutes(urlSection));

        // Also subscribe to future navigations
        const navSub = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
          const updatedSection = this.getUrlSection();
          this.setSubnavRoutes(this.getDefaultSubnavRoutes(updatedSection));
        });

        this.subscription.add(navSub);
      } else {
        this.clearSubnavRoutes();
      }
    });

    this.subscription.add(sub);
  }

  getUrlSection(): string | undefined {
    const url = this.router.url;
    const firstSegment = url.split('/')[1];
    return firstSegment || undefined;
  }

  getDefaultSubnavRoutes(urlSection?: string): { route: string; name: string }[] {
    switch (urlSection) {
      case 'agency':
        return [
          {
            route: '/agency/applications/prime',
            name: 'Prime',
          },
          {
            route: '/agency/applications/sub',
            name: 'Sub',
          },
          {
            route: '/agency/applications/safety',
            name: 'Safety',
          },
        ];

      case 'vendor':
        return [
          {
            route: '/vendor/applications/prime',
            name: 'Prime',
          },
          {
            route: '/vendor/applications/sub',
            name: 'Sub',
          },
          {
            route: '/vendor/applications/safety',
            name: 'Safety',
          },
        ];

      default:
        return [];
    }
  }

  watchNavChanged(): Subject<void> {
    return this._$navChanged;
  }

  onNavChanged() {
    this._$navChanged.next();
  }
}
